import { useCallback, useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { movePanel, type MovePanelResponse } from './api';

/**
 * Position-1 Commit-3 (Sebastian ruling 2026-09-03) — move-panel
 * mutation hook. Mirrors `useSceneTextEdit` in shape and error
 * discipline.
 *
 * Fires `PATCH /api/book/orders/:orderId/scenes/:sceneId/panel-position`
 * when the customer confirms the drag surface's Speichern. The
 * server returns the persisted coords in `response.panelBox`; the
 * client MUST reconcile from that (Sebastian: "PATCH response
 * returns the persisted coords so the client reconciles from
 * truth"), not from its own optimistic drag state.
 *
 * `already_saved` is treated as success — the server short-circuits
 * on identical coords (idempotent retry after a network hiccup).
 */

export type MovePanelResult =
  | { readonly ok: true; readonly response: MovePanelResponse }
  | { readonly ok: false; readonly error: ApiError | null };

export interface UsePanelMoveState {
  readonly saving: boolean;
  readonly error: ApiError | null;
  readonly save: (sceneId: string, left: number, top: number) => Promise<MovePanelResult>;
}

export function usePanelMove(orderId: string): UsePanelMoveState {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const save = useCallback(
    async (sceneId: string, left: number, top: number): Promise<MovePanelResult> => {
      setSaving(true);
      setError(null);
      try {
        const response = await movePanel(orderId, sceneId, left, top);
        return { ok: true, response };
      } catch (err) {
        const apiError = err instanceof ApiError ? err : null;
        setError(apiError);
        return { ok: false, error: apiError };
      } finally {
        setSaving(false);
      }
    },
    [orderId],
  );

  return { saving, error, save };
}
